/**
 * Absolute URLs that match Next.js `trailingSlash: true`.
 * File-like paths (xml, pdf, svg, …) keep the extension and no extra slash.
 */

import { Routes } from '@/config/routes';

const DEFAULT_ORIGIN = 'https://www.qualidoc.com.br';

const STATIC_FILE_SEGMENT =
  /\.(xml|txt|pdf|csv|svg|png|jpe?g|gif|ico|json|webmanifest|woff2?|m?js|cjs|css|map)$/i;

export function getSiteOrigin(): string {
  const raw = process.env.NEXT_PUBLIC_SITE_URL || DEFAULT_ORIGIN;
  try {
    return new URL(raw).origin;
  } catch {
    return DEFAULT_ORIGIN;
  }
}

function isStaticFilePath(pathname: string): boolean {
  const last = pathname.split('/').filter(Boolean).pop() ?? '';
  return STATIC_FILE_SEGMENT.test(last);
}

/**
 * Builds `https://www.qualidoc.com.br/ajuda/` from `"ajuda"` or `"/ajuda"`.
 * Root becomes `https://www.qualidoc.com.br/`.
 */
export function absoluteUrl(path = '/'): string {
  const origin = getSiteOrigin();
  const trimmed = String(path).trim();
  const hashIndex = trimmed.indexOf('#');
  const hash = hashIndex >= 0 ? trimmed.slice(hashIndex + 1) : '';
  const beforeHash = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
  const queryIndex = beforeHash.indexOf('?');
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : '';
  const pathnameOnly =
    queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;

  const collapsed = pathnameOnly.replace(/\/{2,}/g, '/');
  const stripped = collapsed.replace(/^\/+/, '').replace(/\/+$/, '');
  const pathname = stripped === '' ? '/' : `/${stripped}`;
  const withSlash =
    pathname === '/' || isStaticFilePath(pathname) || pathname.endsWith('/')
      ? pathname
      : `${pathname}/`;

  const url = `${origin}${withSlash}${query}`;
  return hash ? `${url}#${hash}` : url;
}

export function productAbsoluteUrl(
  slug: string,
  sku: string | number,
): string {
  // Must match Routes.product encoding so share / canonical / sitemap / JSON-LD
  // never diverge when slug or sku contain reserved characters.
  return absoluteUrl(Routes.product(String(slug), String(sku)));
}

export function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

export function toSitemapLastmodDate(value?: string): string {
  const fallback = new Date().toISOString().split('T')[0];
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toISOString().split('T')[0];
}

export function toSitemapLastmodDatetime(value?: string): string {
  if (value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  }
  return new Date().toISOString();
}
